import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  TablePagination,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  closeGroupLoanService,
  fetchGroupService,
  searchGroupsService,
} from "../../services/groupService";

/* ======================================================
   📊 TABLE COLUMNS
   ====================================================== */
const columns = [
  { id: "slNo", label: "SL.NO", width: 60 },
  { id: "groupName", label: "GROUP NAME", width: 240 },
  { id: "groupCode", label: "GROUP CODE", width: 100 },
  { id: "leader", label: "LEADER NO", width: 130 },
  { id: "agent", label: "AGENT", width: 120 },
  { id: "collectionDay", label: "DAY", width: 90 },
  { id: "loanAmount", label: "LOAN AMOUNT", width: 130 },
  { id: "disbursedAmount", label: "DISBURSED", width: 130 },
  { id: "members", label: "MEMBERS", width: 90 },
  { id: "actions", label: "ACTIONS", width: 200 },
];

const GroupTable = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [groupLoans, setGroupLoans] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [closing, setClosing] = useState(false);

  /* ======================================================
     🔄 FETCH DATA
     ====================================================== */
  const fetchData = async () => {
    const response = searchQuery
      ? await searchGroupsService(page, rowsPerPage, searchQuery)
      : await fetchGroupService(page, rowsPerPage);

    setGroupLoans(response?.groups || []);
    setTotalGroups(response?.totalGroups || 0);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchQuery]);

  /* ======================================================
     🔍 SEARCH
     ====================================================== */
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  }, []);

  /* ======================================================
     🔒 CLOSE LOAN
     ====================================================== */
  const handleOpenCloseModal = (group) => {
    setSelectedGroup(group);
    setCloseModalOpen(true);
  };

  const handleCloseModal = () => {
    setCloseModalOpen(false);
    setSelectedGroup(null);
  };

  const handleConfirmClose = async () => {
    try {
      setClosing(true);
      await closeGroupLoanService(selectedGroup._id);
      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {/* ================= HEADER ================= */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search group name / code / phone"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: 320 }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            onClick={() => navigate("/group/create-group")}
          >
            Create Group
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate("/group/group-pigmy-collection")}
          >
            Pigmy
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate("/group/group-collection")}
          >
            Collection
          </Button>
        </Box>

        {/* ================= TABLE ================= */}
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      fontWeight: 600,
                      color: "#5252ff",
                      width: col.width,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {groupLoans.map((row, index) => (
                <TableRow hover key={row._id}>
                  <TableCell>{index + 1 + page * rowsPerPage}</TableCell>

                  <TableCell
                    sx={{
                      color: "#5252ff",
                      cursor: "pointer",
                      whiteSpace: "wrap",
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    onClick={() =>
                      navigate(`/group/view-group-details/${row._id}`)
                    }
                  >
                    {row.groupName}
                  </TableCell>

                  <TableCell>{row.groupCode}</TableCell>
                  <TableCell>{row.groupLeaderPhoneNumber}</TableCell>
                  <TableCell>{row.agent?.userName}</TableCell>
                  <TableCell>{row.collectionDay}</TableCell>
                  <TableCell>{row.loanAmount}</TableCell>
                  <TableCell>{row.disbursedAmount}</TableCell>
                  <TableCell>{row.users.length}</TableCell>

                  {/* ===== ACTIONS ===== */}
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={row.users.length > 0}
                        onClick={() =>
                          navigate(`/group/update-group/${row._id}`)
                        }
                      >
                        Add
                      </Button>

                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={row.loanStatus === "Closed"}
                        onClick={() => handleOpenCloseModal(row)}
                      >
                        Close
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ================= PAGINATION ================= */}
        <TablePagination
          component="div"
          count={totalGroups}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* ================= CLOSE MODAL ================= */}
      <Dialog open={closeModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Close Group Loan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to close loan for{" "}
            <strong>{selectedGroup?.groupName}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmClose}
            disabled={closing}
          >
            {closing ? "Closing..." : "Yes, Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupTable;
